import React, { useState } from 'react';
import appSettingsService, { ChangeAllRepairColumnResult } from '../../services/appSettings.service';

const SKIP_LABELS: Record<string, string> = {
  edited_since: 'edited after the fan-out — a later save owns the value',
  already_set: 'live column already holds a real datetime',
  bad_newval: 'history value would not parse — needs a manual look',
  no_order_row: 'no ordersall row for the pronumber',
};

/**
 * Change All zero-date repair (Application Settings).
 *
 * The Invoicing-tab Change All fan-out zero-dated invoiceholdfollowupdatetime /
 * marginapproveddatetime; the intended values survive in each pro's history.
 * Preview plans the repair read-only; Commit re-plans server-side and writes with
 * zero guards, so it is safe to re-run and a save racing it always wins.
 */
const ChangeAllRepairPanel: React.FC = () => {
  const [preview, setPreview] = useState<ChangeAllRepairColumnResult[] | null>(null);
  const [applied, setApplied] = useState<boolean>(false);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runPreview = async () => {
    setBusy('preview');
    setError(null);
    setApplied(false);
    setPreview(null);
    try {
      setPreview(await appSettingsService.previewChangeAllRepair());
    } catch {
      setError('Failed to preview the repair.');
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    setBusy('apply');
    setError(null);
    try {
      setPreview(await appSettingsService.applyChangeAllRepair());
      setApplied(true);
    } catch {
      setError('Failed to run the repair.');
    } finally {
      setBusy(null);
    }
  };

  const totalToWrite = (preview ?? []).reduce((sum, c) => sum + c.toWrite, 0);
  const showCommit = !!preview && !applied && totalToWrite > 0;

  return (
    <div className="bg-panel shadow rounded-lg p-6 mt-6">
      <h2 className="text-lg font-medium text-heading mb-1">
        Change All Zero-Date Repair
      </h2>
      <p className="text-xs text-muted mb-4">
        Restores date/datetime fields the Change All fan-out zero-dated (invoice-hold
        follow-up, margin approval, LFD, ETA, document cutoff), using each PRO&apos;s own
        history. Preview shows exactly what a commit would write; commit re-checks every PRO
        server-side, so anything edited in the meantime is left alone. Safe to re-run.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={runPreview}
          disabled={busy !== null}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy === 'preview' ? 'Previewing…' : 'Preview repair'}
        </button>
        {showCommit && (
          <button
            onClick={runApply}
            disabled={busy !== null}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy === 'apply' ? 'Committing…' : `Commit repair (${totalToWrite} writes)`}
          </button>
        )}
      </div>

      {preview && (
        <div className="space-y-4">
          {preview.map((col) => (
            <div
              key={col.column}
              className="border border-gray-200 dark:border-gray-700 rounded-md p-4"
            >
              <h3 className="text-sm font-semibold text-heading">
                {col.column}
              </h3>
              <p className="mt-1 text-sm text-label">
                Candidates: {col.candidates} — to write: {col.toWrite}
              </p>
              {col.written !== undefined && (
                <p className="mt-1 text-sm font-medium text-green-700 dark:text-green-400">
                  Wrote {col.written} of {col.toWrite}.
                </p>
              )}
              {!!col.raced?.length && (
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  Raced (left alone, re-preview to recheck): {col.raced.join(', ')}
                </p>
              )}
              {Object.entries(col.skipped).map(([reason, count]) => (
                <p key={reason} className="mt-1 text-xs text-muted">
                  Skipped {count} — {reason}: {SKIP_LABELS[reason] ?? ''}
                </p>
              ))}
              {col.sample.length > 0 && (
                <div className="mt-2 text-xs text-muted">
                  <span className="font-medium">Sample:</span>
                  <ul className="mt-1 space-y-0.5">
                    {col.sample.map((s) => (
                      <li key={s.pronumber}>
                        {s.pronumber} → {s.value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChangeAllRepairPanel;
